import { AfterViewInit, Component, Input } from '@angular/core';
import {
  App,
  AppFieldDisplayType,
  AppFieldStage,
  AppFieldType,
  AppFormField,
  AppFormFieldAction,
  AppKey,
  AppModal,
  AppProfile,
  AppSetup,
  AppStatus,
  CRM_CONFIG_KEYS,
  CRM_FILTER_OPTIONS,
  CrmFilterOption,
  CrmRequiredField,
  CrmRequiredFieldStatus,
  DEFAULT_CRM_FILTERS
} from './app';
import { NgxSmartModalService } from 'ngx-smart-modal';
import { AppsService } from '../apps.service';
import { TranslateService } from '@ngx-translate/core';
import { DateAgoPipe } from '../../shared/pipes/date-ago.pipe';
import { Router } from '@angular/router';
import { DataStorage } from '../../core/data/data-storage';
import { markAllModified } from '../../core/helpers/form-utils';
import { Subject, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Breakpoint, CaseType, convertCamelCaseToSnakeCase, fromMediaQuery } from 'src/app/core/helpers/utils';
import { UntypedFormArray, UntypedFormGroup } from '@angular/forms';
import { FileFieldData } from './file-field/file-field.component';
import { BusinessService } from '../../core/business/business.service';
import { FieldBehaviour } from '../../shared/display-field/display-field.component';
import { environment } from '../../../environments/environment';
import { MessageService } from 'primeng/api';
import { SessionService } from '../../core/session.service';
import { SubscriptionService } from '../../core/subscription/subscription.service';
import { UserService } from '../../core/user/user.service';

const META_SUPPORT_DOCS_URL =
  'https://heyrosie.com/support/en/articles/16141245-facebook-instagram-have-rosie-answer-your-messenger-and-instagram-dms';

const CRM_SUPPORT_DOCS_URLS: Partial<Record<AppKey, string>> = {
  [AppKey.Hubspot]: 'https://heyrosie.com/support/en/articles/16144385-crm-hubspot-integration',
  [AppKey.GoHighLevel]: 'https://heyrosie.com/support/en/articles/16144387-crm-gohighlevel-integration',
  [AppKey.ZohoCrm]: 'https://heyrosie.com/support/en/articles/16144391-crm-zoho-crm-integration',
  [AppKey.Pipedrive]: 'https://heyrosie.com/support/en/articles/16144392-crm-pipedrive-integration',
  [AppKey.ServiceM8]: 'https://heyrosie.com/support/en/articles/16144395-crm-servicem8-integration',
  [AppKey.Jobber]: 'https://heyrosie.com/support/en/articles/16144396-crm-jobber-integration',
  [AppKey.Bigin]: 'https://heyrosie.com/support/en/articles/16192102-crm-zoho-bigin-integration',
  [AppKey.Kickserv]: 'https://heyrosie.com/support/en/articles/16192108-crm-kickserv-integration',
  [AppKey.HousecallPro]: 'https://heyrosie.com/support/en/articles/16192110-crm-housecall-pro-integration',
  [AppKey.JobTread]: 'https://heyrosie.com/support/en/articles/16416129-crm-jobtread-integration',
  [AppKey.JobNimbus]: 'https://heyrosie.com/support/en/articles/16416141-crm-jobnimbus-integration',
  [AppKey.RepairShopr]: 'https://heyrosie.com/support/en/articles/16794449-crm-repairshopr-integration',
  [AppKey.Nutshell]: 'https://heyrosie.com/support/en/articles/16794490-crm-nutshell-integration',
  [AppKey.Kommo]: 'https://heyrosie.com/support/en/articles/16794520-crm-kommo-integration',
  [AppKey.NoCrm]: 'https://heyrosie.com/support/en/articles/16794607-crm-nocrm-io-integration',
  [AppKey.FollowUpBoss]: 'https://heyrosie.com/support/en/articles/16823341-crm-follow-up-boss-integration',
  [AppKey.AgencyZoom]: 'https://heyrosie.com/support/en/articles/16823342-crm-agencyzoom-integration',
  [AppKey.Workiz]: 'https://heyrosie.com/support/en/articles/17040212-crm-workiz-integration'
};

@Component({
  selector: 'rosie-app',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false
})
export class AppComponent implements AfterViewInit {
  AppStatus = AppStatus;
  AppKey = AppKey;
  @Input() app: App;
  @Input() showDetails = true;

  AppFieldType = AppFieldType;
  AppFormFieldActionEnum = AppFormFieldAction;
  AppModalEnum = AppModal;
  AppFieldDisplayType = AppFieldDisplayType;
  CrmRequiredFieldStatus = CrmRequiredFieldStatus;

  settingsModalClosedSubject: Subject<void> = new Subject<void>();
  isSyncInProgress = false;
  isYelpSetupComplete: boolean | null = null;
  installStep = 1;
  conflictingCalendarApp: App | null = null;
  expandedEventTypes: Set<string> = new Set();

  constructor(public modal: NgxSmartModalService, public apps: AppsService, public business: BusinessService, private translate: TranslateService, private date: DateAgoPipe, private router: Router, private messageService: MessageService, private session: SessionService, public subscription: SubscriptionService, private user: UserService) {
  }

  get appCategory(): string {
    return `APP.CATEGORY.${this.app?.type?.toUpperCase()}`;
  }

  get actionIcon(): string {
    return this.app?.isInstalled || this.app?.isPending ? 'settings' : 'install';
  }

  get iconUrl(): string {
    return `assets/images/${this.actionIcon}.svg`;
  }

  get actionTranslationKey(): string {
    if (this.app?.isInstalled && this.app?.isCrm) {
      return 'APP.SETTINGS';
    }
    return this.app?.isNotInstalled ? 'APP.INSTALL_DEFAULT' : (this.app?.hasExternalFlow ? 'APP.DISCONNECT' : 'APP.SETTINGS');
  }

  get customActionClass(): string {
    return this.app?.isInstalled || this.app?.isPending ? 'secondary slimmish narrow text-sm' : 'btn-primary slimmish narrow text-sm';
  }

  get actionIconName(): string {
    if (this.app?.isNotInstalled) {
      return 'heroPlusCircle';
    }
    if (this.app?.isInstalled || this.app?.isPending && !this.app?.hasExternalFlow) {
      return 'heroCog6Tooth';
    }
    if (this.app?.isInstalled && this.app?.hasExternalFlow) {
      return 'heroLinkSlash';
    }
    return 'heroPlusCircle';
  }

  get actionIconColor(): string {
    return this.app?.isInstalled || this.app?.isPending ? '#6C6176' : '#EAD7FD';
  }

  get lastUpdated(): string {
    return this.date.transform(this.app?.lastUpdated);
  }

  get appDescriptionKey(): string {
    return `APP.${this.app?.key?.toUpperCase()}.DESCRIPTION`;
  }

  get appDetailsKey(): string {
    return `APP.${this.app?.key?.toUpperCase()}.DETAILS`;
  }

  get settingUrl(): string {
    return this.getCapabilityUrl();
  }

  get isMobileVersion(): boolean {
    return this.session.isMobileVersion;
  }

  get isShopifyPendingApproval(): boolean {
    return this.app?.key === AppKey.Shopify && this.subscription.isPendingApproval && this.subscription.isShopifyBillingProvider;
  }

  get isShopifyUnsupportedForProvider(): boolean {
    return this.app?.key === AppKey.Shopify && this.subscription.isExternalBillingProvider && !this.subscription.isShopifyBillingProvider;
  }

  get isShopifyInstalled(): boolean {
    return this.app?.key === AppKey.Shopify && this.app?.isInstalled;
  }

  get providerConfirmationUrl(): string {
    return this.subscription.providerConfirmationUrl;
  }

  openProviderConfirmation(): void {
    if (this.providerConfirmationUrl) {
      window.open(this.providerConfirmationUrl, '_top');
    }
  }

  ngAfterViewInit() {
    this.handleAppStatus();
  }


  action() {
    if (this.app?.hasFormFlow) {
      this.resetForm();
      this.app.error = false;
      this.app.errorMessage = '';
      this.installStep = 1;
    }
    if (this.app?.isNotInstalled && !this.app?.hasExternalFlow) {
      this.modal.open(AppModal.Install, true);
    } else if (this.app?.isNotInstalled && this.app?.hasExternalFlow) {
      // Check for calendar conflict before installing
      if (this.checkCalendarConflict()) {
        this.modal.open(AppModal.CalendarConflict);
      } else if (this.app?.hasFormFields) {
        this.modal.open(AppModal.Install, true);
      } else {
        this.install();
      }
    } else if ((this.app?.isInstalled || this.app?.isPending) && !this.app?.hasExternalFlow) {
      this.modal.open(AppModal.Settings);
    } else if (this.app?.isInstalled && this.app?.hasExternalFlow) {
      this.modal.open(this.app.isCrm ? AppModal.Settings : AppModal.Uninstall);
    }
  }

  verify() {
    if (!this.app.inProgress) {
      this.app.inProgress = true;
      this.apps.verify(this.app.id).subscribe((appStatus) => {
        this.app.status = appStatus;
        this.app.inProgress = false;
        this.handleAppStatus();
        if (this.app.isInstalled) {
          this.modal.closeAll();
        }
      }, () => this.app.inProgress = false);
    }
  }

  getFieldType(formFields: AppFormField[], fieldName: string): string {
    return formFields?.find(field => field.name === fieldName)?.type;
  }

  isFirstVisibleField(form: UntypedFormGroup, formOptions: Record<string, any>, fieldName: string): boolean {
    const visible = Object.keys(form?.controls || {}).filter(key => !formOptions?.[key]?.hidden);
    return visible[0] === fieldName;
  }

  getFieldArrayType(formFields: AppFormField[], fieldName: string): string {
    return formFields?.find(field => field.name === fieldName)?.fields?.[0]?.type;
  }

  getFieldTranslation(fieldName: string): string {
    return this.translate.instant(`APP.${this.app.key?.toUpperCase()}.FIELDS.${convertCamelCaseToSnakeCase(fieldName, CaseType.UpperCase)}`);
  }

  navigateTo(url: string) {
    this.router.navigateByUrl(url);
  }

  navigateToAppointmentSettings() {
    this.router.navigate(['/settings', { section: 'appointments' }]);
  }

  navigateToCommerceSettings() {
    this.router.navigate(['/settings', { section: 'commerce' }]);
  }

  getAppStatus(app: App): string {
    if (app.status === AppStatus.ComingSoon) {
      return 'coming-soon';
    }
    if (this.isYelpSetupInProgress(app)) {
      return 'pending';
    }
    if (app.status === AppStatus.Installed) {
      return 'installed';
    }
    if (app.status === AppStatus.Pending) {
      return 'pending';
    }
    return '';
  }

  getAppStatusCopy(app: App): string {
    if (app.status === AppStatus.ComingSoon) {
      return 'COMING SOON';
    }
    if (this.isYelpSetupInProgress(app)) {
      return 'IN PROGRESS';
    }
    if (app.status === AppStatus.Installed) {
      return 'CONNECTED';
    }
    if (app.status === AppStatus.Pending) {
      return 'PENDING';
    }
    return '';
  }

  private isYelpSetupInProgress(app: App): boolean {
    if (app.key !== AppKey.Yelp || app.status !== AppStatus.Installed) {
      return false;
    }
    return !(this.isYelpSetupComplete ?? !!app.config?.lastMessageOn);
  }

  getHelpTextPerField(fieldName: string): string {
    let fieldKey = fieldName.startsWith('APP.FIELDS') ? fieldName.split('.').pop() : fieldName;
    fieldKey = fieldKey === fieldName ? convertCamelCaseToSnakeCase(fieldKey, CaseType.UpperCase) : fieldKey;
    const appSpecificKey = `APP.${this.app?.key?.toUpperCase()}.FIELDS_HELP.${fieldKey}`;
    const appSpecificTranslation = this.translate.instant(appSpecificKey);
    if (appSpecificTranslation !== appSpecificKey) {
      return appSpecificTranslation;
    }
    const fallbackKey = `APP.FIELDS_DESCRIPTION.${fieldKey}`;
    const translation = this.translate.instant(fallbackKey);
    return translation === fallbackKey ? '' : translation;
  }

  get planRequirement(): string {
    const translationKey = `APP.${this.app?.key?.toUpperCase()}.PLAN_REQUIREMENT`;
    const translation = this.translate.instant(translationKey);
    return translation === translationKey ? '' : translation;
  }

  get temporaryRestriction(): string {
    const translationKey = `APP.${this.app?.key?.toUpperCase()}.TEMPORARY_RESTRICTION`;
    const translation = this.translate.instant(translationKey);
    return translation === translationKey ? '' : translation;
  }

  getFieldValidationErrorTranslation(fieldName: string): string {
    const translationKey = `APP.${this.app.key.toUpperCase()}.VALIDATION.${convertCamelCaseToSnakeCase(fieldName, CaseType.UpperCase)}`;
    const translation = this.translate.instant(translationKey);
    return translation === translationKey ? 'COMMON.VALIDATION.REQUIRED' : translationKey;
  }

  handleAppStatus(hasActionFailed = false, isUpdate = false) {
    if (this.app && !this.app.isIntegrating) {
      if (DataStorage.persistent().hasKey(`installInProgress-${this.getAppIdentificator(this.app)}`)) {
        if (this.app.isNotInstalled || hasActionFailed) {
          this.app.error = true;
          if (!isUpdate) {
            // Check if there's a persisted error message from the backend
            const persistedError = DataStorage.persistent().get(`installError-${this.app.id}`);
            const errorMessage = persistedError || this.connectionFailedFallback();
            this.messageService.add({ severity: 'warn', detail: errorMessage, life: 5000 });

            // Clean up the persisted error
            if (persistedError) {
              DataStorage.persistent().remove(`installError-${this.app.id}`);
            }
          }
        } else if (this.app.isInstalled) {
          this.app.error = false;
          this.messageService.add({ severity: 'success', detail: `Successfully connected to ${this.app.name}`, life: 5000 });
        } else if (this.app.isPending) {
          this.app.error = false;
          this.messageService.add({ severity: 'warn', detail: `${this.app.name} is in pending state. Follow the instructions to complete the installation.`, life: 5000 });
        }
        if (!isUpdate && !this.app.error) {
          this.apps.refreshApp(this.app);
        }
        DataStorage.persistent().remove(`installInProgress-${this.getAppIdentificator(this.app)}`);
      }
    }
  }

  close(): void {
    this.modal.closeAll();
  }

  install(isUpdate = false) {
    if (this.app?.hasFormFields) {
      markAllModified(this.app.setupData.form);
    }
    if (this.app?.hasCredentialsFlow) {
      this.apps.install(this.app).subscribe((formFields) => {
        this.app.setupData.formFields.push({ name: 'Api Key', value: formFields.apiKey || '', behaviour: FieldBehaviour.Plain, type: AppFieldType.Text, actions: [AppFormFieldAction.Copy], visibility: AppFieldStage.Always });
        DataStorage.persistent().set(`installInProgress-${this.getAppIdentificator(this.app)}`, 'true');
      })
    } else if (this.app?.hasFormFlow || (isUpdate && this.app?.hasAnyEditableFieldsOnceInstalled)) {
      this.handleFormActionInstall(isUpdate);
    } else if (this.app?.hasExternalFlow) {
      this.externalFlowActionInstall();
    }
  }

  closeInstallModal() {
    if (this.app?.hasCredentialsFlow) {
      this.verify();
    }
  }

  update() {
    if (this.app.hasAnyEditableFieldsOnceInstalled) {
      this.modal.open(AppModal.Update);
    } else if (this.app.hasExternalFlow) {
      this.externalFlowActionInstall();
    } else {
      this.resetForm();
      if (this.app.setupData.form) {
        markAllModified(this.app.setupData.form, true);
      }
      this.modal.open(AppModal.Update);
    }
  }

  uninstall() {
    if (!this.app.inProgress) {
      this.app.inProgress = true;
      this.apps.uninstall(this.app).subscribe(() => {
        this.app.uninstall();
        this.modal.closeAll();
        this.app.inProgress = false;
        this.messageService.add({ severity: 'success', detail: `${this.app.name} successfully disconnected.`, life: 5000 });
        this.apps.refreshApp(this.app);
      });
    }
  }

  addFormField(fieldName: string) {
    const formArray = this.app?.setupData?.form?.get(fieldName) as UntypedFormArray;
    const arrayField = this.app?.setupData?.formFields?.find(f => f.name === fieldName)?.fields[0];
    this.apps.addFormArrayField(formArray, arrayField?.name, arrayField?.isRequired);
  }

  removeFormField(fieldName: string, index: number) {
    const formArray = this.app?.setupData?.form?.get(fieldName) as UntypedFormArray;
    this.apps.removeFormArrayField(formArray, index);
  }

  redirectToUrl(url: string) {
    this.openWebsite(url);
  }

  updateAppProfile(data) {
    this.apps.updateAppProfile(this.app, data).subscribe((appDetails: AppProfile) => {
      this.app.details.profile = appDetails;
      this.modal.closeLatestModal();
    });
  }

  handleSettingsModalClose() {
    this.settingsModalClosedSubject.next();
  }

  unsorted() {
  }

  handleFileUpload(event: { data: File, fieldName: string }) {
    this.app.setupData.form.get(event.fieldName).setValue(event.data);
  }

  isFieldEditable(fieldName: string): boolean {
    return this.app.isFieldEditable(fieldName);
  }

  getFileFieldData(fieldName: string): FileFieldData {
    return {
      label: this.getFieldTranslation(fieldName),
      value: this.app.setupData.form.get(fieldName).value,
      key: fieldName
    };
  }

  getFieldAdditionalInformation(fieldName: string): string {
    return this.app?.setupData?.formFields?.find(field => field.name === fieldName)?.additionalInformation;
  }

  getFieldDisplayType(fieldName: string): string {
    return this.app?.setupData?.formFields?.find(field => field.name === fieldName)?.displayType;
  }

  getSelectPlaceholder(fieldName: string): string {
    const appSpecificKey = `APP.${this.app?.key?.toUpperCase()}.FIELDS_PLACEHOLDER.${convertCamelCaseToSnakeCase(fieldName, CaseType.UpperCase)}`;
    const appSpecificTranslation = this.translate.instant(appSpecificKey);
    if (appSpecificTranslation !== appSpecificKey) {
      return appSpecificTranslation;
    }
    return this.translate.instant('APP.FIELDS.SELECT_OPTION', { value: this.getFieldTranslation(fieldName) });
  }

  previousInstallStep() {
    this.app.error = false;
    this.app.errorMessage = '';
    this.installStep = 1;
  }

  handleFieldSelectOptionChanged(fieldName: string, value: string | number) {
    if (this.app.hasExternalFlowWithFormFields) {
      DataStorage.persistent().set(`field-${this.getAppIdentificator(this.app)}-${fieldName}`, value);
    }
  }

  initCredentialsFlow() {
    if (this.app.hasCredentialsFlow) {
      this.install();
    }
  }

  getLogo(app: App): string {
    return `assets/images/${app.referenceId}.svg`;
  }

  get configEntries(): Array<{ key: string; value: any }> {
    if (!this.app?.config) {
      return [];
    }
    return Object.entries(this.app.config)?.map(([key, value]) => ({
      key,
      value: Array.isArray(value) ? value : (value?.toString() || '')
    }));
  }

  getConfigTranslationKey(configKey: string): string {
    return `APP.${this.app?.key?.toUpperCase()}.CONFIG.${convertCamelCaseToSnakeCase(configKey, CaseType.UpperCase)}`;
  }

  getConfigTooltip(configKey: string): string {
    const tooltipKey = `${this.getConfigTranslationKey(configKey)}_TOOLTIP`;
    const translation = this.translate.instant(tooltipKey);
    return translation === tooltipKey ? '' : translation;
  }

  trackByKey(index: number, item: { key: string; value: string }): string {
    return item.key;
  }

  isConfigValueArray(value: any): boolean {
    return Array.isArray(value);
  }

  formatConfigArrayItem(item: any): string {
    if (typeof item === 'string' || typeof item === 'number') {
      return item.toString();
    }
    if (typeof item === 'object' && item !== null) {
      // If it's an object, try to get a name or label property, otherwise stringify it
      return item.name || item.label || item.value || JSON.stringify(item, null, 2);
    }
    return String(item);
  }

  getResyncTooltip(): string {
    const appKey = this.app?.key?.toUpperCase();
    const appSpecificTooltipKey = `APP.${appKey}.RESYNC_TOOLTIP`;
    const appSpecificTranslation = this.translate.instant(appSpecificTooltipKey);

    // If app-specific tooltip exists, use it, otherwise fall back to generic
    if (appSpecificTranslation !== appSpecificTooltipKey) {
      return appSpecificTranslation;
    }

    if (this.hasCrmRequiredFields) {
      return this.translate.instant('APP.CRM.REQUIRED_FIELDS.RESYNC_TOOLTIP', { appName: this.app?.name });
    }

    return this.translate.instant('APP.RESYNC.TOOLTIP', { appName: this.app?.name });
  }

  // Event types with questions helper methods
  get eventTypes(): any[] {
    return this.app?.config?.eventTypes || [];
  }

  get hasEventTypes(): boolean {
    return this.eventTypes.length > 0;
  }

  get hasUnsupportedQuestions(): boolean {
    return this.eventTypes.some((et: any) => this.hasEventTypeIncompatibilities(et));
  }

  hasEventTypeIncompatibilities(eventType: any): boolean {
    if (eventType?.valid === false) {
      return true;
    }
    if (this.getEventTypeUnsupportedFields(eventType).length > 0) {
      return true;
    }
    const questions = eventType?.customQuestions || [];
    return questions.some((q: any) => q.valid === false);
  }

  hasEventTypeUnsupportedQuestions(eventType: any): boolean {
    const questions = eventType?.customQuestions || [];
    return questions.some((q: any) => q.valid === false);
  }

  getEventTypeUnsupportedFields(eventType: any): any[] {
    return eventType?.unsupported_fields || [];
  }

  hasEventTypeUnsupportedFields(eventType: any): boolean {
    return this.getEventTypeUnsupportedFields(eventType).length > 0;
  }

  getEventTypeQuestions(eventType: any): any[] {
    return eventType?.customQuestions || [];
  }

  getEventTypeQuestionsCount(eventType: any): number {
    return this.getEventTypeQuestions(eventType).length;
  }

  isQuestionValid(question: any): boolean {
    return question?.valid !== false;
  }

  isEventTypeExpanded(eventType: any): boolean {
    return this.expandedEventTypes.has(eventType.uri || eventType.name);
  }

  toggleEventTypeExpansion(eventType: any): void {
    const key = eventType.uri || eventType.name;
    if (this.expandedEventTypes.has(key)) {
      this.expandedEventTypes.delete(key);
    } else {
      this.expandedEventTypes.add(key);
    }
  }

  isEventTypesConfigKey(key: string): boolean {
    return key === 'eventTypes' || key === 'availableEventTypes' || key === 'connectedEventTypes';
  }

  get nonEventTypeConfigEntries(): Array<{ key: string; value: any }> {
    return this.configEntries.filter(entry => !this.isEventTypesConfigKey(entry.key) && !this.isCrmConfigKey(entry.key));
  }

  isCrmConfigKey(key: string): boolean {
    return !!this.app?.isCrm && CRM_CONFIG_KEYS.includes(key);
  }

  get activeCrmFilters(): CrmFilterOption[] {
    const filters = { ...DEFAULT_CRM_FILTERS, ...(this.app?.config?.crmFilters || {}) };
    return CRM_FILTER_OPTIONS.filter(option => filters[option.key]);
  }

  get hasCrmRequiredFields(): boolean {
    return !!this.app?.isCrm && Array.isArray(this.app?.config?.requiredFields);
  }

  get crmRequiredFields(): CrmRequiredField[] {
    return this.hasCrmRequiredFields ? this.app.config.requiredFields : [];
  }

  get crmRequiredFieldsLabel(): string {
    const appSpecificKey = `APP.${this.app?.key?.toUpperCase()}.REQUIRED_FIELDS_LABEL`;
    const appSpecificTranslation = this.translate.instant(appSpecificKey);
    return appSpecificTranslation !== appSpecificKey ? appSpecificTranslation : this.translate.instant('APP.CRM.REQUIRED_FIELDS.LABEL');
  }

  get crmRequiredFieldsEmptyKey(): string {
    if (this.app?.config?.requiredFieldsSyncFailed) {
      return 'APP.CRM.REQUIRED_FIELDS.NOT_READ';
    }
    return this.app?.config?.requiredFieldsSyncedOn ? 'APP.CRM.REQUIRED_FIELDS.NONE' : 'APP.CRM.REQUIRED_FIELDS.NOT_CHECKED';
  }

  get crmRequiredFieldsHoldNewContacts(): boolean {
    return this.app?.config?.requiredFieldsHoldNewContacts !== false;
  }

  get crmRequiredFieldsTooltipKey(): string {
    return this.crmRequiredFieldsHoldNewContacts ? 'APP.CRM.REQUIRED_FIELDS.TOOLTIP' : 'APP.CRM.REQUIRED_FIELDS.TOOLTIP_OPTIONAL';
  }

  get crmUnavailableFieldLabels(): string[] {
    return this.crmRequiredFields
      .filter(field => field.status === CrmRequiredFieldStatus.Unavailable)
      .map(field => field.label);
  }

  get crmBlockingFieldLabels(): string[] {
    return this.crmRequiredFieldsHoldNewContacts ? this.crmUnavailableFieldLabels : [];
  }

  get hasCrmRequiredFieldsSyncFailed(): boolean {
    return this.hasCrmRequiredFields && !!this.app?.config?.requiredFieldsSyncFailed;
  }

  getCrmRequiredFieldStatusKey(status: CrmRequiredFieldStatus): string {
    return `APP.CRM.REQUIRED_FIELDS.STATUS.${status?.toUpperCase()}`;
  }

  reconnect(): void {
    this.externalFlowActionInstall();
  }

  get hasSchedulingAppWithEventTypes(): boolean {
    return this.app?.isScheduling && this.hasEventTypes;
  }

  getSupportDocsLink(): { label: string; value: string } | null {
    if (!this.app) {
      return null;
    }

    const crmSupportDocsUrl = CRM_SUPPORT_DOCS_URLS[this.app.key];
    if (crmSupportDocsUrl) {
      return {
        label: `Learn more about our ${this.app.name} integration in our Help Center.`,
        value: crmSupportDocsUrl
      };
    }

    switch (this.app.key) {
      case AppKey.Appointlet:
        return {
          label: 'Appointlet Support Docs',
          value: 'https://heyrosie.com/support/en/articles/13134186-scheduling-appointlet-integration'
        };
      case AppKey.Acuity:
        return {
          label: 'Acuity Support Docs',
          value: 'https://heyrosie.com/support/en/articles/13162068-scheduling-acuity-integration'
        };
      case AppKey.GoogleCalendar:
        return {
          label: 'Google Calendar Support Docs',
          value: 'https://heyrosie.com/support/en/articles/12872287-scheduling-google-calendar-integration'
        };
      case AppKey.Calendly:
        return {
          label: 'Calendly Support Docs',
          value: 'https://heyrosie.com/support/en/articles/12998554-scheduling-calendly-integration'
        };
      case AppKey.FacebookMessenger:
        return {
          label: 'Learn more about our Facebook Messenger integration in our Help Center.',
          value: META_SUPPORT_DOCS_URL
        };
      case AppKey.Instagram:
        return {
          label: 'Learn more about our Instagram integration in our Help Center.',
          value: META_SUPPORT_DOCS_URL
        };
      default:
        return null;
    }
  }

  private getAppIdentificator(app: App): string {
    return app.id;
  }

  private connectionFailedFallback(): string {
    if (this.app?.key === AppKey.ServiceM8) {
      return 'The ServiceM8 connection didn’t finish. If you’re on ServiceM8’s free plan, you’ll need a paid plan first — that’s where API access starts. Otherwise, just click Connect to try again.';
    }
    if (this.app?.isCrm) {
      return `The ${this.app.name} connection didn’t finish. If you canceled on the ${this.app.name} side or the window closed early, just click Connect to try again.`;
    }
    return `Something went wrong while connecting to ${this.app.name}. Please try connecting again.`;
  }

  synchronize() {
    if (!this.isSyncInProgress) {
      this.isSyncInProgress = true;
      this.apps.synchronize(this.app).subscribe(() => {
        this.isSyncInProgress = false;
        this.messageService.add({ severity: 'success', detail: `Successfully synced ${this.app.name}`, life: 5000 });
        this.apps.refreshApp(this.app);
      }, () => {
        this.isSyncInProgress = false;
        const detail = this.hasCrmRequiredFields && this.app.hasExternalFlow
          ? this.translate.instant('APP.CRM.REQUIRED_FIELDS.SYNC_FAILED_OAUTH_TOAST', { appName: this.app.name })
          : `Failed to sync ${this.app.name}. Please try again.`;
        this.messageService.add({ severity: 'error', detail, life: 5000 });
        if (this.app.isCrm) {
          this.apps.refreshApp(this.app);
        }
      });
    }
  }

  private externalFlowActionInstall() {
    const isValidForm = this.app.hasFormFields ? this.app.setupData.form.valid : true;
    if (!this.app.inProgress && isValidForm) {
      this.app.inProgress = true;
      this.apps.getUrl(this.app).subscribe((setup: AppSetup) => {
        DataStorage.persistent().set(`installInProgress-${this.getAppIdentificator(this.app)}`, 'true');
        this.app.setupData.url = setup.url;
        console.log(this.app.setupData.url);
        this.redirectToUrl(this.app.setupData.url);
      }, (err) => {
        this.app.error = true;
        this.app.inProgress = false;
        const serverMessage = typeof err === 'string' ? err : err?.error?.message;
        this.messageService.add({
          severity: 'error',
          detail: serverMessage || `Failed to start the ${this.app.name} installation. Please try again.`,
          life: 5000
        });
      });
    }
  }

  private handleFormActionInstall(isUpdate = false) {
    if (this.app.hasConditionalRequiredFields()) {
      this.app.setConditionalRequiredFields();
    }
    const form = this.app.setupData.form;
    if (!this.app.inProgress && (!form || form.valid)) {
      DataStorage.persistent().set(`installInProgress-${this.getAppIdentificator(this.app)}`, 'true');
      this.app.inProgress = true;
      this.app.error = false;
      this.app.errorMessage = '';
      this.apps.install(this.app, form ? form.getRawValue() : {})
        .pipe(catchError((err) => {
          this.app.inProgress = false;
          this.app.errorMessage = this.installErrorMessage(err);
          return throwError(() => new Error('Something went wrong, please try again.'));
        }))
        .subscribe(() => {
          this.app.inProgress = false;
          this.app.status = AppStatus.Installed;
          this.handleAppStatus();
          this.modal.getModal(isUpdate ? AppModal.Update : AppModal.Install).close();
        }, () => {
          this.app.error = true;
          DataStorage.persistent().remove(`installInProgress-${this.getAppIdentificator(this.app)}`);
        });
    }
  }

  /**
   * The HTTP interceptor rethrows 4xx responses as a plain message string
   * (or its own generic fallback), so the API's actionable copy only reaches
   * the modal when the string form is read too. The interceptor's generic
   * fallback is dropped so the app-specific INSTALL_FAILED copy shows instead.
   */
  private installErrorMessage(err: any): string {
    const message = typeof err === 'string' ? err : (err?.error?.message || err?.message || '');
    return message === 'Something went wrong, please try again' ? '' : message;
  }

  private resetForm() {
    this.apps.resetForm(this.app);
    const form = this.app.setupData.form;
    if (!form) {
      return;
    }
    const formValues = this.getFormValues(this.app.setupData.formFields);
    form.patchValue(formValues);
  }

  nextInstallStep() {
    const form = this.app.setupData.form;
    const sources = this.app.discoverySourceFieldNames;
    sources.forEach(name => {
      form.get(name)?.markAsTouched();
      form.get(name)?.updateValueAndValidity();
    });
    if (this.app.inProgress || sources.some(name => form.get(name)?.invalid)) {
      return;
    }
    const values = {};
    sources.forEach(name => values[name] = form.get(name)?.value);
    this.app.inProgress = true;
    this.app.error = false;
    this.app.errorMessage = '';
    this.apps.discoverFormOptions(this.app, values).subscribe((options) => {
      this.app.inProgress = false;
      this.app.discoverableFields.forEach(field => {
        field.options = options[field.name] || [];
        const control = form.get(field.name);
        if (control && !field.options.some(option => option.value === control.value)) {
          control.setValue(field.options.length === 1 ? field.options[0].value : null);
        }
      });
      this.installStep = 2;
    }, (err) => {
      this.app.inProgress = false;
      this.app.error = true;
      this.app.errorMessage = err?.error?.message || '';
    });
  }

  private getFormValues(formFields: AppFormField[]): any {
    const formValues = {};
    formFields?.forEach(field => {
      if (field.type === AppFieldType.Array) {
        const fieldValue = field.value && Array.isArray(field.value) ? field.value : [];
        formValues[field.name] = fieldValue.map(value => {
          return value[field.fields[0].name];
        });
      } else {
        formValues[field.name] = this.app.getFieldValue(field);
      }
    });
    return formValues;
  }

  private getCapabilityUrl(): string {
    try {
      return `${window.location.origin}/settings;section=capabilities`;
    } catch (error) {
      return '/settings;section=capabilities';
    }
  }

  private openWebsite(url): void {
    try {
      window.location.href = url;
    } catch (error) {
      window.open(url, '_blank');
    }
  }

  private checkCalendarConflict(): boolean {
    // Only check for calendar apps
    if (!this.app?.isScheduling) {
      return false;
    }

    // Find if another calendar provider is already installed
    this.conflictingCalendarApp = this.apps.allApps.find(app =>
      app.isScheduling &&
      app.key !== this.app.key &&
      (app.isInstalled || app.isPending)
    );

    return !!this.conflictingCalendarApp;
  }

  proceedWithCalendarInstall(): void {
    // Uninstall the conflicting calendar app first, then proceed with install
    if (this.conflictingCalendarApp) {
      this.apps.uninstall(this.conflictingCalendarApp).subscribe(() => {
        this.conflictingCalendarApp.uninstall();
        this.conflictingCalendarApp = null;
        this.install();
      }, () => {
        // If uninstall fails, still try to proceed with the new install
        this.conflictingCalendarApp = null;
        this.install();
      });
    } else {
      this.install();
    }
  }
}
